import { useState, useCallback, useMemo, useEffect } from 'react';
import { Transaction, PriceMap, PortfolioSnapshot } from '../types';
import { computeHoldings, computePortfolioStats, buildPortfolioHistory } from '../utils/calculations';
import { buildDisplayRateTable } from '../utils/displayRates';
import { loadCachedRates } from './useExchangeRates';
import { CURRENCY_CHANGED_EVENT, loadCurrencyCode } from '../context/CurrencyContext';
import { CLOUD_DATA_HYDRATED_EVENT } from '../lib/cloud/reconcile';
import { getCacheOwner } from '../lib/cloud/localCache';
import { fxRepairDone, isStaleCapture, markFxRepairDone, planFxRepair } from '../utils/fxRepair';

const TX_KEY = 'mycryptoplanner_transactions';
const SNAPSHOTS_KEY = 'mycryptoplanner_snapshots';

function loadTransactions(): Transaction[] {
  try {
    const raw = localStorage.getItem(TX_KEY);
    return raw ? (JSON.parse(raw) as Transaction[]) : [];
  } catch {
    return [];
  }
}

function saveTransactions(txs: Transaction[]) {
  localStorage.setItem(TX_KEY, JSON.stringify(txs));
}

function loadSnapshots(): PortfolioSnapshot[] {
  try {
    const raw = localStorage.getItem(SNAPSHOTS_KEY);
    return raw ? (JSON.parse(raw) as PortfolioSnapshot[]) : [];
  } catch {
    return [];
  }
}

function saveSnapshots(snaps: PortfolioSnapshot[]) {
  // Keep last 365 days
  const trimmed = snaps.slice(-365);
  localStorage.setItem(SNAPSHOTS_KEY, JSON.stringify(trimmed));
}

export function usePortfolio(prices: PriceMap) {
  const [transactions, setTransactions] = useState<Transaction[]>(loadTransactions);
  const [snapshots, setSnapshots] = useState<PortfolioSnapshot[]>(loadSnapshots);

  // The login reconcile may hydrate cloud transactions/snapshots into localStorage
  // after this hook has already initialised (it lives above the auth render-gate).
  // Re-read from localStorage when the reconcile signals it has finished.
  useEffect(() => {
    const reread = () => {
      setTransactions(loadTransactions());
      setSnapshots(loadSnapshots());
    };
    window.addEventListener(CLOUD_DATA_HYDRATED_EVENT, reread);
    return () => window.removeEventListener(CLOUD_DATA_HYDRATED_EVENT, reread);
  }, []);

  // ── One-time repair of rates captured past the RBA bundle's end ────────────
  //
  // See utils/fxRepair. Runs after the login reconcile has hydrated this user's
  // transactions from the cloud (so it repairs the cloud's rows, and the write
  // below is pushed back up by the write-through, the same way every edit is).
  // Recorded per user once every affected row is fixed; if ECB is unreachable
  // for some, nothing is recorded and the next load tries again. Re-running on
  // repaired data finds nothing, so it is idempotent either way.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const owner = getCacheOwner();
      if (!owner || fxRepairDone(owner)) return;
      const { patches, fixed, failed } = await planFxRepair(loadTransactions());
      if (cancelled) return;
      if (fixed > 0) {
        // The normal update path: state + localStorage, which the write-through
        // mirrors to Supabase. A patch applies only to a row that is STILL stale,
        // so an edit made while the rates were being fetched is never overwritten.
        setTransactions((prev) => {
          let changed = false;
          const next = prev.map((t) => {
            const patch = patches[t.id];
            if (!patch || !isStaleCapture(t)) return t;
            changed = true;
            return { ...t, ...patch };
          });
          if (!changed) return prev;
          saveTransactions(next);
          return next;
        });
      }
      if (fixed > 0 || failed > 0) {
        // eslint-disable-next-line no-console
        console.info(
          `[fx-repair] re-captured the exchange rate on ${fixed} transaction(s) dated after the RBA bundle` +
            (failed > 0 ? `; ${failed} could not be resolved and will be retried next load` : '')
        );
      }
      if (failed === 0) markFxRepairDone(owner, fixed);
    };
    window.addEventListener(CLOUD_DATA_HYDRATED_EVENT, run);
    return () => {
      cancelled = true;
      window.removeEventListener(CLOUD_DATA_HYDRATED_EVENT, run);
    };
  }, []);

  // Snapshot today's value when prices load
  useEffect(() => {
    if (Object.keys(prices).length === 0) return;
    const holdings = computeHoldings(transactions, prices);
    const totalValue = holdings.reduce((s, h) => s + h.totalValue, 0);
    if (totalValue === 0) return;

    const today = new Date().toISOString().split('T')[0];
    setSnapshots((prev) => {
      const filtered = prev.filter((s) => s.date !== today);
      const next = [...filtered, { date: today, value: totalValue }];
      saveSnapshots(next);
      return next;
    });
  }, [prices, transactions]);

  const addTransaction = useCallback(
    (tx: Omit<Transaction, 'id' | 'timestamp'>) => {
      const newTx: Transaction = {
        ...tx,
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        timestamp: new Date(tx.date).getTime(),
      };
      setTransactions((prev) => {
        const next = [...prev, newTx].sort((a, b) => a.timestamp - b.timestamp);
        saveTransactions(next);
        return next;
      });
    },
    []
  );

  const removeTransaction = useCallback((id: string) => {
    setTransactions((prev) => {
      const next = prev.filter((t) => t.id !== id);
      saveTransactions(next);
      return next;
    });
  }, []);

  const updateTransaction = useCallback((id: string, updates: Omit<Transaction, 'id' | 'timestamp'>) => {
    setTransactions((prev) => {
      const next = prev.map((t) => {
        if (t.id !== id) return t;
        return { ...t, ...updates, timestamp: new Date(updates.date).getTime() };
      }).sort((a, b) => a.timestamp - b.timestamp);
      saveTransactions(next);
      return next;
    });
  }, []);

  /**
   * The display currency and today's spot, read WITHOUT the currency context.
   *
   * This hook runs above CurrencyProvider (App.tsx builds the portfolio before the
   * providers wrap it), so it cannot call useCurrency. Both values have
   * module-level accessors for exactly this situation — `loadCachedRates` already
   * exists for it — and CURRENCY_CHANGED_EVENT tells us when to re-read, the same
   * way CLOUD_DATA_HYDRATED_EVENT does for transactions.
   */
  const [currencyCode, setCurrencyCode] = useState<string>(loadCurrencyCode);
  useEffect(() => {
    const reread = () => setCurrencyCode(loadCurrencyCode());
    window.addEventListener(CURRENCY_CHANGED_EVENT, reread);
    window.addEventListener(CLOUD_DATA_HYDRATED_EVENT, reread);
    return () => {
      window.removeEventListener(CURRENCY_CHANGED_EVENT, reread);
      window.removeEventListener(CLOUD_DATA_HYDRATED_EVENT, reread);
    };
  }, []);

  const spotPerUsd = currencyCode === 'USD' ? 1 : loadCachedRates()?.[currencyCode] ?? 1;

  /**
   * One table per (currency, spot) pair, memoised so the per-date rate lookups are
   * paid once rather than on every price tick. `computeHoldings` runs very often
   * and asks for a rate per transaction, so without this the roll-back loop inside
   * `lookupRatesSync` would run thousands of times a second.
   */
  const displayRates = useMemo(
    () => buildDisplayRateTable(currencyCode, spotPerUsd),
    [currencyCode, spotPerUsd]
  );

  const holdings = useMemo(
    () => computeHoldings(transactions, prices, displayRates),
    [transactions, prices, displayRates]
  );

  const stats = useMemo(() => computePortfolioStats(holdings), [holdings]);

  const history = useMemo(
    () => buildPortfolioHistory(transactions, prices, snapshots),
    [transactions, prices, snapshots]
  );

  return {
    transactions,
    holdings,
    stats,
    history,
    addTransaction,
    removeTransaction,
    updateTransaction,
  };
}
